import React, { useEffect, useState } from "react";
import Countdown, { zeroPad } from "react-countdown";
import Button from "../../../components/ui/Button";
import OtpBox from "../../../components/ui/OtpBox";

import { VERIFY_QUOTE_CARD_CLASS } from "../styles";
import { getOtpInstructionText, getSendOtpPromptText } from "../utils";
import { formatQuoteDateTime } from "../../remittance-beta/utils/avanseQuote";

interface OtpVerificationProps {
  maskedContact?: string;
  sendType?: string;
  smsType?: string;
  otpSent: boolean;
  otpValue: string;
  setOtpValue: React.Dispatch<React.SetStateAction<string>>;
  otpError: boolean;
  verifyOtpError: string;
  isOTPVerified: boolean;
  isLoading: boolean;
  sendOtpLoading: boolean;
  onSendOtp: () => void;
  onVerify: () => void;
  onResend: () => void;
}

const OtpVerification: React.FC<OtpVerificationProps> = ({
  maskedContact,
  sendType,
  smsType,
  otpSent,
  otpValue,
  setOtpValue,
  otpError,
  verifyOtpError,
  isOTPVerified,
  isLoading,
  sendOtpLoading,
  onSendOtp,
  onVerify,
  onResend,
}) => {
  const [date, setDate] = useState(Date.now() + 120000);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (otpSent) {
      setDate(Date.now() + 120000);
      setResetKey((prev) => prev + 1);
    }
  }, [otpSent]);

  const otpInstruction = getOtpInstructionText(sendType, maskedContact, smsType);
  const sendOtpPrompt = getSendOtpPromptText(sendType, smsType);

  const handleResendOtp = () => {
    onResend();
    setDate(Date.now() + 120000);
    setResetKey((prev) => prev + 1);
  };

  return (
    <div className={`${VERIFY_QUOTE_CARD_CLASS} p-8`}>
      <div className="flex-1">
        <h2 className="text-xl font-bold text-[#111] font-inter mb-2">Verify Quote</h2>

        {!otpSent ? (
          <p className="text-sm text-[#444] font-inter leading-relaxed">{sendOtpPrompt}</p>
        ) : (
          <>
            <p className="text-sm text-[#444] font-inter leading-relaxed">
              {otpInstruction.prefix}
              {otpInstruction.contact ? (
                <>
                  {" "}
                  <span className="font-semibold text-[#111]">{otpInstruction.contact}</span>
                </>
              ) : null}
              .
            </p>

            <div className="mt-8">
              <p className="text-sm font-semibold text-[#111] font-inter mb-3">
                Enter OTP <span className="text-red-100">*</span>
              </p>

              <div className="w-full [&>div]:w-full [&>div>div]:flex [&>div>div]:flex-nowrap [&>div>div]:justify-between [&>div>div]:gap-2 [&_input]:!w-[48px] [&_input]:!h-[52px] [&_input]:!mr-0 [&_input]:!shrink-0 sm:[&_input]:!w-[52px]">
                <OtpBox
                  updateValue={(value) => setOtpValue(value)}
                  errorMsg={verifyOtpError}
                  errorMessageClassname={
                    otpError ? "border-none bg-transparent text-center mt-2" : "text-center mt-2"
                  }
                  numberOfBoxes={6}
                  otpBoxClassName="!bg-[#f5f5f5]/10 !border-[0.5px] !border-[#d9d9d9] !rounded-xl !text-[#111]"
                />
              </div>
            </div>

            {isOTPVerified ? (
              <p className="text-success font-inter font-medium text-sm mt-4">OTP Verified</p>
            ) : null}
          </>
        )}
      </div>

      <div className="mt-auto pt-8 w-full">
        {!otpSent ? (
          <Button
            bgColor="bg-primary-1050"
            fontStyle={{
              fontFamily: "font-inter",
              fontSize: "text-base",
              fontWeight: "font-semibold",
              fontColor: "text-white disabled:text-white",
            }}
            borderStyle={{
              borderWidth: "border",
              borderRadius: "rounded",
              borderColor: "border-[#0F5BF1] disabled:border-[#9F9F9F]",
            }}
            className="w-full h-[46px] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={sendOtpLoading}
            onClickHandler={onSendOtp}
            isLoading={sendOtpLoading}
          >
            Send OTP
          </Button>
        ) : (
          <>
            <div className="mb-6">
              <Countdown
                key={resetKey}
                date={date}
                renderer={({ minutes, seconds, completed }) => {
                  if (completed) {
                    return (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        className="text-sm font-bold text-[#969696] cursor-pointer font-inter hover:text-[#666]"
                      >
                        Resend OTP
                      </button>
                    );
                  }

                  return (
                    <div className="flex items-center gap-2 text-sm font-bold text-[#969696] font-inter">
                      <span>Resend OTP</span>
                      <span>
                        {zeroPad(minutes)} : {zeroPad(seconds)}
                      </span>
                    </div>
                  );
                }}
              />
            </div>

            <Button
              bgColor="bg-primary-1050"
              fontStyle={{
                fontFamily: "font-inter",
                fontSize: "text-base",
                fontWeight: "font-semibold",
                fontColor: "text-white disabled:text-white",
              }}
              borderStyle={{
                borderWidth: "border",
                borderRadius: "rounded",
                borderColor: "border-[#0F5BF1] disabled:border-[#9F9F9F]",
              }}
              className="w-full h-[46px] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={otpValue.length < 6 || isLoading}
              onClickHandler={onVerify}
              isLoading={isLoading}
            >
              Submit
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

interface VerifiedStateProps {
  message?: string;
  createdOn?: string | null;
  quoteVerifiedDate?: string;
}

export const QuoteVerifiedPanel: React.FC<VerifiedStateProps> = ({
  message,
  createdOn,
  quoteVerifiedDate,
}) => (
  <div className={`${VERIFY_QUOTE_CARD_CLASS} p-8`}>
    <div className="flex flex-1 flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <h2 className="text-xl font-bold text-[#1F8A4C] font-inter mb-2">Quote Verified</h2>
        <p className="text-sm text-[#444] font-inter">
          {message || "This quote has been successfully verified."}
        </p>
      </div>

      <div className="shrink-0 space-y-3 text-sm sm:text-right">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[#888] font-inter">
            Quote sent date
          </p>
          <p className="mt-1 font-medium text-[#333] font-inter">
            {formatQuoteDateTime(createdOn)}
          </p>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[#1F8A4C] font-inter">
            Quote Verified date
          </p>
          <p className="mt-1 font-semibold text-[#1F8A4C] font-inter">
            {formatQuoteDateTime(quoteVerifiedDate)}
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default OtpVerification;
